"use client";

import { useState } from "react";
import type { ContentQueueItem } from "@/lib/api";
import { LIBRARY_TYPE_KEYS, LIBRARY_TYPE_LABEL } from "@/lib/libraryFormatters";

export interface LibraryEditPayload {
  title?: string;
  description?: string;
  url?: string;
  content_type?: string;
  duration_min?: number;
}

interface Props {
  detail: ContentQueueItem;
  isBusy: boolean;
  onCancel: () => void;
  onSubmit: (payload: LibraryEditPayload) => void;
}

export default function LibraryEditModal({ detail, isBusy, onCancel, onSubmit }: Props) {
  const [title, setTitle] = useState(detail.title);
  const [description, setDescription] = useState(detail.description ?? "");
  const [url, setUrl] = useState(detail.url ?? "");
  const [contentType, setContentType] = useState(detail.content_type ?? "");
  const [durationMin, setDurationMin] = useState<number | "">(detail.duration_min ?? "");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    const payload: LibraryEditPayload = {};
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title cannot be empty.");
      return;
    }
    if (trimmedTitle !== detail.title) payload.title = trimmedTitle;
    const trimmedDescription = description.trim();
    if (trimmedDescription !== (detail.description ?? "").trim()) {
      payload.description = trimmedDescription;
    }
    const trimmedUrl = url.trim();
    if (trimmedUrl !== (detail.url ?? "").trim()) {
      payload.url = trimmedUrl;
    }
    if (contentType && contentType !== (detail.content_type ?? "")) {
      payload.content_type = contentType;
    }
    const numericDuration = durationMin === "" ? null : Number(durationMin);
    if (numericDuration !== null && numericDuration !== detail.duration_min) {
      if (numericDuration < 1 || numericDuration > 480) {
        setError("Duration must be between 1 and 480 minutes.");
        return;
      }
      payload.duration_min = numericDuration;
    }
    if (Object.keys(payload).length === 0) {
      onCancel();
      return;
    }
    setError(null);
    onSubmit(payload);
  }

  return (
    <div
      data-testid="library-edit-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/25 backdrop-blur-[2px] px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-edit-title"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-2 rounded-t-2xl bg-forest-med" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="rounded-full bg-sage-pale px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-forest-med">
              Edit
            </span>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h2 id="library-edit-title" className="sr-only">
            Edit library item
          </h2>

          <div className="mt-4 space-y-3">
            <Field label="Title">
              <input
                type="text"
                data-testid="library-edit-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={300}
                className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
              />
            </Field>

            <Field label="Description / notes">
              <textarea
                data-testid="library-edit-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={8000}
                rows={4}
                placeholder="Add your notes or summary…"
                className="w-full resize-y rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select
                  data-testid="library-edit-type-input"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
                >
                  <option value="">— pick a type —</option>
                  {LIBRARY_TYPE_KEYS.map((typeKey) => (
                    <option key={typeKey} value={typeKey}>
                      {LIBRARY_TYPE_LABEL[typeKey]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Duration (min)">
                <input
                  type="number"
                  data-testid="library-edit-duration-input"
                  value={durationMin}
                  onChange={(e) =>
                    setDurationMin(e.target.value === "" ? "" : Math.max(1, Number(e.target.value) || 0))
                  }
                  min={1}
                  max={480}
                  className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
                />
              </Field>
            </div>

            <Field label="URL">
              <input
                type="url"
                data-testid="library-edit-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                maxLength={2000}
                className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
              />
            </Field>
          </div>

          {error && (
            <p className="mt-3 rounded-xl bg-amber/10 px-3 py-2 text-xs text-amber">{error}</p>
          )}

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              data-testid="library-edit-save"
              onClick={handleSubmit}
              disabled={isBusy}
              className="flex-1 rounded-full bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
            >
              {isBusy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isBusy}
              className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">{label}</span>
      {children}
    </label>
  );
}
